import axios from 'axios';
import { fetchAuthSession, fetchUserAttributes, signOut } from 'aws-amplify/auth';
import { clearUser } from '../../store/slices/userSlice';

let store;
let persistor;
// Guards against the interceptor firing logout() many times in parallel
// (every in-flight request that 401s / has no session hits it at once),
// which previously caused repeated redirects.
let isLoggingOut = false;

const API_URL = process.env.REACT_APP_BACKEND_URL || "";
const apiConfig = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});


const isTokenExpired = (session) => {
  if (!session?.tokens?.accessToken?.payload?.exp) return true;
  const currentTime = Math.floor(Date.now() / 1000);
  const tokenExpiration = session.tokens.accessToken.payload.exp;
  return tokenExpiration < currentTime;
};

const refreshAccessToken = async () => {
  try {
    const newSession = await fetchAuthSession();
    return newSession?.tokens?.accessToken?.toString();
  } catch (error) {
    console.error('Error refreshing access token:', error);
    if (error.name === 'NotAuthorizedException' && error.message.includes('Access Token has been revoked')) {
      console.log({ error })
      logout();
    }
    return null;
  }
};

const logout = async () => {
  if (isLoggingOut) return;
  isLoggingOut = true;
  try {
    await signOut();
  } catch (error) {
    console.error('Error during logout:', error);
  }
  // Clear the persisted Redux auth state too - not just the Cognito
  // session. Otherwise redux-persist rehydrates the stale
  // { isAuthenticated: true, user } after the reload below, Login.js
  // immediately navigates back to the dashboard, its first API call
  // 401s and lands here again -> /login <-> /dashboard redirect loop.
  try {
    if (store) store.dispatch(clearUser());
    if (persistor && typeof persistor.flush === 'function') {
      await persistor.flush();
    }
  } catch (error) {
    console.error('Error clearing persisted auth state:', error);
  }
  window.location.href = '/login';
};

// Helper function to extract user ID from AWS Cognito attributes
const getUserFromAWS = (userData) => {
  if (!userData) return null;
  return {
    uid: userData.sub || userData['custom:uid'] || null,
    email: userData.email || null,
  };
};


// Function to set the store reference
export const setStore = (storeInstance) => {
  store = storeInstance;
};

// Function to set the redux-persist persistor reference (so logout() can
// flush the cleared auth state to storage before the full page reload).
export const setPersistor = (persistorInstance) => {
  persistor = persistorInstance;
};

const initializeApiConfig = async () => {
  try {
    apiConfig.interceptors.request.use(
      async (config) => {
        // Allow selected public endpoints (e.g. pre-login checks) to bypass auth headers.
        if (config?.skipAuth === true) {
          if (config.data instanceof FormData) {
            delete config.headers['Content-Type'];
          }
          return config;
        }

        let session;
        try {
          session = await fetchAuthSession();
        } catch (e) {
          console.error("No valid session found", e);
          await logout();
          return Promise.reject(new Error("No valid session"));
        }

        // A session object can come back with no tokens when the user is
        // not actually signed in. Bail out cleanly here rather than letting
        // fetchUserAttributes() throw an uncaught
        // "User needs to be authenticated to call this API." further down.
        if (!session?.tokens?.accessToken) {
          console.log("No access token in session. Logging out user.");
          await logout();
          return Promise.reject(new Error("No valid session"));
        }

        // uid header is best-effort — never let its lookup fail the request.
        let uid;
        try {
          const userData = await fetchUserAttributes();
          ({ uid } = getUserFromAWS(userData) || {});
        } catch (e) {
          console.warn("Could not fetch user attributes for uid header", e);
        }
        let accessToken = session?.tokens?.accessToken?.toString();

        if (isTokenExpired(session)) {
          accessToken = await refreshAccessToken();
          if (!accessToken) throw new Error("Failed to refresh access token");
        }
        if (!accessToken || accessToken.trim() === '') {
          console.log("Empty or null token detected. Logging out user.");
          await logout();
          return Promise.reject(new Error("No valid access token available"));
        }

        if (accessToken) {
          config.headers['Authorization'] = 'Bearer ' + accessToken;

        }

        if (uid) {
          config.headers.uid = uid;
        }

        // If the data is FormData, let axios set the Content-Type automatically
        // This is crucial for file uploads to work properly
        if (config.data instanceof FormData) {
          delete config.headers['Content-Type'];
          // Axios will automatically set it to 'multipart/form-data' with boundary
        }

        return config;
      },
      (error) => Promise.reject(error)
    );

    // Add response interceptor to handle token revocation and 401 errors globally
    apiConfig.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.config?.skipAuth === true) {
          return Promise.reject(error);
        }
        // Don't logout on network errors or if we're on the login page
        const isLoginPage = window.location.pathname.includes('/login');
        
        if (error.code === 'ERR_NETWORK' || error.code === 'ERR_CONNECTION_REFUSED') {
          console.log("Network error - backend may be unreachable");
          return Promise.reject(error);
        }
        
        // Only logout on 401 if it's a real authentication failure AND not on login page
        if (error.response?.status === 401 && !isLoginPage) {
          // Check if it's an actual auth failure (not just missing uid header during initial load)
          const errorMessage = error.response?.data?.error || '';
          if (errorMessage !== 'uid header is required') {
            console.log("401 Unauthorized: Real authentication failure. Logging out user.");
            await logout();
          }
          return Promise.reject(error);
        }
        
        // Check if the error is token revocation from AWS Cognito
        if (error.response?.data?.__type === "NotAuthorizedException" &&
          error.response?.data?.message === "Access Token has been revoked") {
          console.log("Access token has been revoked. Logging out user.");
          await logout();
          return Promise.reject(error);
        }
        
        return Promise.reject(error);
      }
    );
  } catch (error) {
    console.error('Error initializing API config:', error);
  }
};

export const setupAxios = async () => {
  await initializeApiConfig();
};

setupAxios();

export default apiConfig;
